import React, { useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { TriangleAlert } from 'lucide-react';
import { AlertBanner, ui } from '../../../components/ui';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import { useGeoLocation } from '../hooks/useGeoLocation';
import { useLocalityDisplay } from '../hooks/useLocalityDisplay';
import { submitYesterdayCheckout } from '../../../services/attendanceService';

const inputClass = 'mt-1 h-10 w-full rounded-xl border border-border bg-background px-3 text-sm read-only:bg-muted';

export default function YesterdayCheckoutModal({ record, onSuccess }) {
  const { latitude, longitude, error: geoError, isLoading: geoLoading, refetch } = useGeoLocation();
  const { locality, status: localityStatus, error: localityError, refetch: retryLocality } = useLocalityDisplay(latitude, longitude);
  const [checkoutTime, setCheckoutTime] = useState('');
  const [location, setLocation] = useState(record.checkOutLocation || record.checkInLocation || '');
  const [activities, setActivities] = useState(record.activitiesCompleted || '');
  const [tomorrowPlan, setTomorrowPlan] = useState(record.tomorrowPlan || '');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const pending = useRef(false);
  // Preserve the server's local attendance date; do not convert it through UTC.
  // The backend serializes LocalDateTime with a space and an offset suffix.
  // Compare local wall-clock values, matching the checkout LocalDateTime payload.
  const checkInParts = String(record.checkInTime || '').match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}:\d{2}(?:\.\d+)?)/);
  const checkInLocal = checkInParts ? checkInParts[1] + 'T' + checkInParts[2] : '';
  const checkInDisplay = checkInParts ? checkInParts[2].slice(0, 5) : String(record.checkInTime || '');
  const date = record.attendanceDate || record.checkInTime?.slice(0, 10) || '';
  const requestedCheckOutTime = checkoutTime ? date + 'T' + checkoutTime + ':00' : '';
  const afterCheckIn = Boolean(requestedCheckOutTime && checkInLocal && new Date(requestedCheckOutTime) > new Date(checkInLocal));
  const locationReady = !geoLoading && !geoError && latitude != null && longitude != null && localityStatus === 'success' && locality;
  const canSubmit = Boolean(record.attendanceId && date && afterCheckIn && location.trim() && activities.trim() && tomorrowPlan.trim() && reason.trim() && locationReady && !submitting);

  const handleSubmit = async event => {
    event.preventDefault();
    if (!canSubmit || pending.current) return;
    pending.current = true;
    setSubmitting(true);
    setError('');
    try {
      await submitYesterdayCheckout({
        attendanceId: Number(record.attendanceId), requestedCheckOutTime,
        reason: reason.trim(), activitiesCompleted: activities.trim(),
        tomorrowPlan: tomorrowPlan.trim(), location: location.trim(),
        latitude, longitude, geoLocation: locality,
      });
      onSuccess();
    } catch (err) {
      setError(err.message || 'Unable to submit yesterday’s checkout. Please try again.');
    } finally {
      pending.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Dialog.Root open>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[9000] bg-black/60" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-[9001] max-h-[90vh] w-[calc(100vw-32px)] max-w-xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl border border-border bg-background p-6 text-foreground shadow-2xl"
          onEscapeKeyDown={event => event.preventDefault()}
          onInteractOutside={event => event.preventDefault()}
        >
          <Dialog.Title className="flex items-center gap-2 text-xl font-semibold"><TriangleAlert className="h-5 w-5 shrink-0 text-red-500" aria-hidden="true" />Mark Yesterday's Checkout</Dialog.Title>
          <Dialog.Description className="mt-2 mb-5 text-sm text-muted-foreground">Submit the below details to continue.</Dialog.Description>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <fieldset disabled={submitting} className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <label className="block text-sm font-medium">Date<input aria-label="Attendance date" type="date" readOnly value={date} className={inputClass} /></label>
                <label className="block text-sm font-medium">Check-in<input type="text" readOnly value={checkInDisplay} className={inputClass} /></label>
                <label className="block text-sm font-medium">Check-out *<input type="time" required value={checkoutTime} onChange={event => setCheckoutTime(event.target.value)} className={inputClass} /></label>
              </div>
              {checkoutTime && !afterCheckIn && <AlertBanner type="error">Checkout time must be after the recorded check-in time.</AlertBanner>}
              <label className="block text-sm font-medium">Location Name *<input required maxLength={40} value={location} onChange={event => setLocation(event.target.value)} placeholder="e.g. Head Office" className={inputClass} /></label>
              {geoError || localityError ? (
                <AlertBanner type="error">{geoError || localityError} <button type="button" onClick={geoError ? refetch : retryLocality} className="underline">Retry location</button></AlertBanner>
              ) : <p role="status" className="text-sm text-muted-foreground">{locationReady ? 'Current location: ' + locality : 'Fetching your current location...'}</p>}
              <label className="block text-sm font-medium">Activities Completed Yesterday *<Textarea required value={activities} onChange={event => setActivities(event.target.value)} className="mt-1 rounded-xl" /></label>
              <label className="block text-sm font-medium">Today Plan *<Textarea required value={tomorrowPlan} onChange={event => setTomorrowPlan(event.target.value)} className="mt-1 rounded-xl" /></label>
              <label className="block text-sm font-medium">Reason for missed checkout *<Textarea required value={reason} onChange={event => setReason(event.target.value)} className="mt-1 rounded-xl" /></label>
            </fieldset>
            {error && <AlertBanner type="error">{error}</AlertBanner>}
            <div className="flex justify-end"><Button type="submit" disabled={!canSubmit} className={ui.primaryBtn}>{submitting ? 'Submitting...' : 'Submit for Approval'}</Button></div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}